import * as React from 'react';
import { FormTextField, TextFieldProps } from 'ui';
import { useFormikContext } from 'formik';
import Anchor from 'components/Anchor';
import emailSpellChecker from '@zootools/email-spell-checker';

interface Props extends TextFieldProps {
  onUseSuggestion?(): void;
}

export const FormEmailField: React.FunctionComponent<Props> = ({
  name = 'email',
  id = 'email',
  label = 'Email',
  fullWidth,
  inputProps,
  placeholder = 'Email address',
  autoComplete = 'email',
  onUseSuggestion,
  ...rest
}) => {
  const [suggestion, setSuggestion] = React.useState<string | undefined>(undefined);
  const formik = useFormikContext();

  const testid = inputProps?.['data-testid'] ?? 'email-input';

  const resetSuggestion = () => {
    setSuggestion(undefined);
  };

  const handleUseMailcheckSuggestion = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    formik.setFieldValue(name, suggestion);
    resetSuggestion();
    if (onUseSuggestion) {
      onUseSuggestion();
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;
    const suggestion = emailSpellChecker.run({ email: value });
    if (suggestion) {
      setSuggestion(suggestion.full);
    } else {
      resetSuggestion();
    }
  };

  const mailcheckSuggestion = suggestion ? (
    <span>
      Did you mean{' '}
      <Anchor href="#" onClick={handleUseMailcheckSuggestion}>
        {suggestion}
      </Anchor>
      ?
    </span>
  ) : undefined;

  return (
    <FormTextField
      fullWidth={fullWidth}
      name={name}
      id={id}
      type="email"
      placeholder={placeholder}
      autoComplete={autoComplete}
      label={label}
      onBlur={handleBlur}
      inputProps={{
        'data-testid': testid,
      }}
      helperText={mailcheckSuggestion}
      {...rest}
    />
  );
};
